import {motion} from "framer-motion"
import {FC, useEffect, useState} from "react"

import {Starter} from "./getContextualStarters"
import {WIDGET_BUBBLE_SIZE, WIDGET_MARGIN} from "./styles"

interface Props {
  starters: Starter[]
  onSelect: (message: string) => void
  onDismiss: () => void
}

const MAX_CONVERSATION_STARTERS = 2

/** Delay before showing dismiss so users notice starters first */
const CLOSE_BUTTON_REVEAL_MS = 2500

const STARTERS_CARD_CLASS = "ri2-guru-starters-card"
const STARTERS_CARD_CSS = `
  @keyframes ri2GuruStartersBorderSpin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  @keyframes ri2GuruStartersGlow {
    0%, 100% { box-shadow: 4px 4px 24px rgba(0, 225, 255, 0.18), 4px 4px 24px rgba(0, 0, 0, 0.20); }
    50% { box-shadow: 4px 4px 28px rgba(0, 225, 255, 0.28), 4px 4px 24px rgba(0, 0, 0, 0.22); }
  }
  .${STARTERS_CARD_CLASS} {
    position: relative;
    overflow: hidden;
    border-radius: 14px;
    animation: ri2GuruStartersGlow 2.8s ease-in-out infinite;
  }
  .${STARTERS_CARD_CLASS}::before {
    content: "";
    position: absolute;
    inset: -55%;
    z-index: 0;
    pointer-events: none;
    background: conic-gradient(
      from 90deg,
      #3A8BC4 0deg,
      #00E1FF 66deg,
      #91CCF0 118deg,
      #3A8BC4 178deg,
      #91CCF0 232deg,
      #81DDF7 268deg,
      #70EEFF 307deg,
      #3A8BC4 360deg
    );
    animation: ri2GuruStartersBorderSpin 8s linear infinite;
  }
  .${STARTERS_CARD_CLASS} > * {
    position: relative;
    z-index: 1;
  }
`

/** Approximates SVG `paint0_linear_265_804` for the card rim */
const CARD_BORDER_GRADIENT =
  "linear-gradient(135deg, #015DA0 0%, #00E1FF 18%, #91CCF0 33%, #015DA0 50%, #91CCF0 64%, #81DDF7 75%, #70EEFF 85%, #00A9D7 100%)"

const HEADER_GRADIENT = "linear-gradient(90deg, #015DA0 0%, #009EC8 100%)"

const chipStyle = (index: number): React.CSSProperties => ({
  background: index === 0 ? "#F5F5F5" : "#FFFFFF",
  border: "1px solid #00A9D7",
  borderRadius: 7,
  padding: "9px 12px",
  textAlign: "left",
  cursor: "pointer",
  fontSize: 13,
  color: "#333333",
  lineHeight: 1.3,
  transition: "border-color 0.15s, background-color 0.15s",
  width: "100%",
  fontFamily: "inherit",
  minHeight: 38,
  WebkitTapHighlightColor: "transparent",
})

/** Hex + four-point sparkle from widget SVG export */
const HeaderHexSparkleIcon: FC = () => (
  <svg
    width={28}
    height={24}
    viewBox="38 34 28 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{flexShrink: 0}}
    aria-hidden
  >
    <path
      d="M58.8 34H45.04L38 45.84L44.72 58H58.8L65.84 45.84L58.8 34Z"
      fill="#00A9D7"
    />
    <path
      d="M51.76 54.4802C50.9578 50.5469 47.0969 46.6329 43.12 45.8399C47.0969 45.0474 50.9578 41.1335 51.76 37.2002C52.5617 41.1335 56.4231 45.0474 60.4 45.8399C56.4231 46.6324 52.5622 50.5469 51.76 54.4797V54.4802Z"
      fill="white"
    />
  </svg>
)

/** Filled X from conversation-starters SVG export (matches design asset) */
const CloseIcon: FC = () => (
  <svg
    width={15}
    height={15}
    viewBox="320.941 38.443 15.118 15.117"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path
      d="M335.816 39.5684C336.059 39.3262 336.059 38.9277 335.816 38.6855C335.574 38.4434 335.176 38.4434 334.934 38.6855L328.5 45.1191L322.066 38.6855C321.824 38.4434 321.426 38.4434 321.184 38.6855C320.941 38.9277 320.941 39.3262 321.184 39.5684L327.617 46.002L321.184 52.4355C320.941 52.6777 320.941 53.0762 321.184 53.3184C321.426 53.5605 321.824 53.5605 322.066 53.3184L328.5 46.8848L334.934 53.3184C335.176 53.5605 335.574 53.5605 335.816 53.3184C336.059 53.0762 336.059 52.6777 335.816 52.4355L329.383 46.002L335.816 39.5684Z"
      fill="currentColor"
    />
  </svg>
)

export const ConversationStarters: FC<Props> = ({
  starters,
  onSelect,
  onDismiss,
}) => {
  const [showCloseButton, setShowCloseButton] = useState(false)

  useEffect(() => {
    const id = window.setTimeout(
      () => setShowCloseButton(true),
      CLOSE_BUTTON_REVEAL_MS,
    )
    return (): void => window.clearTimeout(id)
  }, [])

  return (
    <motion.div
      initial={{opacity: 0, y: 10}}
      animate={{opacity: 1, y: 0}}
      exit={{opacity: 0, y: 10}}
      transition={{type: "spring", stiffness: 300, damping: 25, delay: 0.4}}
      style={{
        position: "fixed",
        bottom: WIDGET_MARGIN + WIDGET_BUBBLE_SIZE + 16,
        right: WIDGET_MARGIN,
        width: 300,
        zIndex: 9997,
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        backdropFilter: "blur(5px)",
        WebkitBackdropFilter: "blur(5px)",
      }}
    >
      <style>{STARTERS_CARD_CSS}</style>
      <div
        className={STARTERS_CARD_CLASS}
        style={{
          borderRadius: 14,
          padding: 2,
          background: CARD_BORDER_GRADIENT,
          boxShadow: "4px 4px 24px rgba(0, 0, 0, 0.24)",
        }}
      >
        <div
          style={{
            borderRadius: 12,
            overflow: "hidden",
            background: "#FFFFFF",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "6px 10px 6px 12px",
              background: HEADER_GRADIENT,
            }}
          >
            <HeaderHexSparkleIcon />
            <p
              style={{
                margin: 0,
                flex: 1,
                fontSize: 15,
                fontWeight: 500,
                color: "#FFFFFF",
                lineHeight: 1.2,
              }}
            >
              {"Need personalized help?"}
            </p>
            {showCloseButton ? (
              <motion.button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  onDismiss()
                }}
                aria-label="Dismiss conversation starters"
                initial={{opacity: 0}}
                animate={{opacity: 1}}
                transition={{duration: 0.25}}
                style={{
                  flexShrink: 0,
                  width: 30,
                  height: 30,
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  color: "#FFFFFF",
                  lineHeight: 1,
                  padding: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: 6,
                }}
              >
                <CloseIcon />
              </motion.button>
            ) : (
              <div aria-hidden style={{flexShrink: 0, width: 30, height: 30}} />
            )}
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 8,
              padding: 10,
              background: "#FFFFFF",
            }}
          >
            {starters
              .slice(0, MAX_CONVERSATION_STARTERS)
              .map(({label, message}, index) => (
                <motion.button
                  key={label}
                  onClick={() => onSelect(message)}
                  initial={{opacity: 0, x: 10}}
                  animate={{opacity: 1, x: 0}}
                  transition={{delay: 0.5 + index * 0.08}}
                  style={chipStyle(index)}
                >
                  {label}
                </motion.button>
              ))}
          </div>
        </div>
      </div>
    </motion.div>
  )
}
